/**
 * Factual Dispute Item Selection Screen
 * 
 * Plain-English, user-confirmation driven interface for factual disputes.
 * Focused on "what looks wrong / unauthorized / not mine".
 * 
 * Sub-types:
 * 1. General Factual Dispute
 * 2. Identity Theft Dispute
 * 3. Identity Theft / Data Breach Dispute
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  FileText,
  Shield,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Info,
  HelpCircle,
  Upload,
  Trash2,
  Plus,
  User,
  CreditCard,
  Building2,
  Search,
  Lock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Factual dispute sub-types
const FACTUAL_SUB_TYPES = [
  {
    id: 'general',
    name: 'General Factual Dispute',
    icon: FileText,
    description: 'Use for general reporting errors, unauthorized activity, and plain-English factual challenges.',
    color: 'emerald',
  },
  {
    id: 'identity_theft',
    name: 'Identity Theft Dispute',
    icon: Shield,
    description: 'Use for fraudulent accounts, fraudulent inquiries, and addresses or personal information resulting from identity theft.',
    color: 'amber',
  },
  {
    id: 'data_breach',
    name: 'Identity Theft / Data Breach Dispute',
    icon: AlertTriangle,
    description: 'Use for identity-theft-related disputes tied to known exposure events, situations where compromised personal information may have contributed to fraudulent reporting.',
    color: 'red',
  },
];

// Issue type options for user confirmation
const ISSUE_TYPES = [
  { value: 'not_recognized', label: 'I do not recognize this' },
  { value: 'inaccurate', label: 'This is inaccurate' },
  { value: 'identity_theft', label: 'This resulted from identity theft' },
  { value: 'should_not_be_on_report', label: 'This information should not be on my report' },
  { value: 'unauthorized', label: 'This was unauthorized' },
  { value: 'already_resolved', label: 'This was already resolved' },
];

// Recognition status options
const RECOGNITION_OPTIONS = [
  { value: 'yes', label: 'Yes', color: 'text-emerald-400' },
  { value: 'no', label: 'No', color: 'text-red-400' },
  { value: 'not_sure', label: 'Not sure', color: 'text-amber-400' },
];

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700',
  Experian: 'bg-amber-100 text-amber-700',
  Equifax: 'bg-red-100 text-red-700',
};

// Factual item card component
const FactualItemCard = ({ 
  item, 
  isIncluded, 
  onToggleInclude, 
  onUpdateDetails,
  itemDetails,
}) => {
  const [expanded, setExpanded] = useState(false);
  const details = itemDetails || {};
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl overflow-hidden transition-all ${
        isIncluded 
          ? 'border-emerald-500/50 bg-emerald-500/5' 
          : 'border-slate-700 bg-stone-800/50'
      }`}
      data-testid={`factual-item-${item.id}`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-white truncate">
                {item.creditor_name || item.name || 'Unknown Item'}
              </h4>
              <Badge variant="outline" className="text-xs">
                {item.type || 'Account'}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Badge className={BUREAU_COLORS[item.bureau] || 'bg-slate-700 text-slate-300'}>
                {item.bureau}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={isIncluded ? "default" : "outline"}
              size="sm"
              onClick={() => onToggleInclude(item.id)}
              className={isIncluded ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {isIncluded ? (
                <>
                  <Check size={14} className="mr-1" />
                  Included
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-1" />
                  Include
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* AI Reason */}
        <div className="mt-3 p-3 bg-slate-700/30 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
            <Info size={12} />
            Why this was selected:
          </p>
          <p className="text-sm text-slate-300">
            {item.ai_reason || 'This item may need your confirmation before it is included.'}
          </p>
        </div>
        
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-sm text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expanded ? 'Hide details' : 'Add details & documents'}
        </button>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-700"
          >
            <div className="p-4 space-y-4">
              {/* Recognition Question */}
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">
                  Do you recognize this item?
                </Label>
                <div className="flex gap-2">
                  {RECOGNITION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => onUpdateDetails(item.id, { recognition: option.value })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        details.recognition === option.value
                          ? option.value === 'yes' 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                            : option.value === 'no'
                              ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                              : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Issue Type */}
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">
                  What best describes the issue?
                </Label>
                <Select
                  value={details.issueType || ''}
                  onValueChange={(value) => onUpdateDetails(item.id, { issueType: value })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select an issue type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Optional Details */}
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">
                  Optional supporting details
                </Label>
                <Textarea
                  value={details.notes || ''}
                  onChange={(e) => onUpdateDetails(item.id, { notes: e.target.value })}
                  placeholder="Add any additional information about this item..."
                  className="bg-slate-700 border-slate-600 text-white"
                  rows={3}
                />
              </div>
              
              {/* Document Prompts */}
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">
                  Optional supporting documents
                </Label>
                <div className="flex flex-wrap gap-2">
                  {['Police Report', 'ID Theft Affidavit', 'Billing Statement', 'Other Proof'].map((doc) => (
                    <button
                      key={doc}
                      onClick={() => {
                        const docs = details.documentTypes || [];
                        const updated = docs.includes(doc) 
                          ? docs.filter(d => d !== doc)
                          : [...docs, doc];
                        onUpdateDetails(item.id, { documentTypes: updated });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        (details.documentTypes || []).includes(doc)
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      <Upload size={12} />
                      {doc}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onToggleInclude(item.id, false)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 size={14} className="mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Data breach follow-up component
const DataBreachFollowUp = ({ onSelect, selectedBreach }) => {
  const breachOptions = [
    {
      id: 'equifax',
      name: 'Equifax-related exposure',
      description: 'The 2017 Equifax data breach that exposed personal information of millions.',
    },
    {
      id: 'other_breach',
      name: 'Another known breach / exposure',
      description: 'A different data breach or known exposure event.',
    },
    {
      id: 'not_sure',
      name: 'Not sure which one',
      description: "I believe my information was compromised but I'm not certain of the source.",
    },
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium text-sm">Data Breach Connection</p>
            <p className="text-slate-400 text-sm mt-1">
              Selecting a breach source helps us tailor your dispute wording appropriately.
            </p>
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {breachOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.id)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
              selectedBreach === option.id
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-700 bg-stone-800/50 hover:border-slate-600'
            }`}
            data-testid={`breach-option-${option.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-white">{option.name}</h4>
                <p className="text-sm text-slate-400 mt-1">{option.description}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedBreach === option.id
                  ? 'border-amber-500 bg-amber-500'
                  : 'border-slate-600'
              }`}>
                {selectedBreach === option.id && <Check size={12} className="text-white" />}
              </div>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// Main component
const FactualDisputeSelectionScreen = ({
  findings,
  onContinue,
  onBack,
}) => {
  // State
  const [subType, setSubType] = useState(null);
  const [selectedBreach, setSelectedBreach] = useState(null);
  const [includedItems, setIncludedItems] = useState(new Set());
  const [itemDetails, setItemDetails] = useState({});
  const [breachFollowUp, setBreachFollowUp] = useState({
    noticedWhen: '',
    receivedNotice: null,
    involvesFraudulent: [],
    hasDocuments: null,
  });
  
  // Categorize findings
  const categorizedFindings = {
    general: findings?.filter(f => 
      !f.issue_type?.includes('identity') && 
      !f.issue_type?.includes('personal_info')
    ) || [],
    identity: findings?.filter(f => 
      f.issue_type?.includes('identity') || 
      f.issue_type?.includes('fraud') ||
      f.issue_type?.includes('unauthorized')
    ) || [],
    personalInfo: findings?.filter(f => 
      f.issue_type?.includes('personal_info') ||
      f.issue_type?.includes('address') ||
      f.issue_type?.includes('name')
    ) || [],
  };
  
  // Initialize included items from recommendations
  useEffect(() => {
    const recommended = new Set(
      findings?.filter(f => f.recommended)?.map(f => f.id) || []
    );
    setIncludedItems(recommended);
  }, [findings]);
  
  const toggleInclude = (itemId, include) => {
    setIncludedItems(prev => {
      const newSet = new Set(prev);
      if (include === false || newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  const updateItemDetails = (itemId, updates) => {
    setItemDetails(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...updates },
    }));
  };
  
  const handleContinue = () => {
    onContinue({
      subType,
      selectedBreach: subType === 'data_breach' ? selectedBreach : null,
      breachFollowUp: subType === 'data_breach' ? breachFollowUp : null,
      includedItems: Array.from(includedItems),
      itemDetails,
    });
  };
  
  // Step 1: Sub-type selection
  if (!subType) {
    return (
      <div className="min-h-screen bg-stone-900 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <FileText size={32} className="text-emerald-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Factual Dispute</h1>
              <p className="text-slate-400 max-w-xl mx-auto">
                Choose the type of factual dispute that best matches your situation.
              </p>
            </div>
            
            {/* Sub-type selection */}
            <div className="space-y-4">
              {FACTUAL_SUB_TYPES.map((type, index) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setSubType(type.id)}
                    className={`w-full text-left p-6 rounded-xl border-2 transition-all ${
                      type.color === 'emerald'
                        ? 'border-slate-700 hover:border-emerald-500/50 hover:bg-emerald-500/5'
                        : type.color === 'amber'
                          ? 'border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/5'
                          : 'border-slate-700 hover:border-red-500/50 hover:bg-red-500/5'
                    } bg-stone-800/50`}
                    data-testid={`subtype-${type.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        type.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                        type.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        <Icon size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">{type.name}</h3>
                        <p className="text-slate-400 text-sm mt-1">{type.description}</p>
                      </div>
                      <ChevronDown size={20} className="text-slate-500 rotate-[-90deg]" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
            
            {/* Navigation */}
            <div className="mt-8 flex justify-between">
              <Button variant="ghost" onClick={onBack}>
                <ArrowLeft size={18} className="mr-1" />
                Back
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // Step 2: Data breach follow-up (if data_breach selected)
  if (subType === 'data_breach' && !selectedBreach) {
    return (
      <div className="min-h-screen bg-stone-900 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-red-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Data Breach Information</h1>
              <p className="text-slate-400 max-w-xl mx-auto">
                Select the breach or exposure event related to your situation.
              </p>
            </div>
            
            <DataBreachFollowUp 
              selectedBreach={selectedBreach}
              onSelect={setSelectedBreach}
            />
            
            {/* Navigation */}
            <div className="mt-8 flex justify-between">
              <Button variant="ghost" onClick={() => setSubType(null)}>
                <ArrowLeft size={18} className="mr-1" />
                Back
              </Button>
              
              {selectedBreach && (
                <Button onClick={() => {}} className="bg-emerald-600 hover:bg-emerald-700">
                  Continue
                  <ArrowRight size={18} className="ml-1" />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // Step 3: Item selection
  const recommendedCount = findings?.filter(f => f.recommended)?.length || 0;
  const needsConfirmation = findings?.filter(f => !f.confirmed)?.length || 0;
  const identityRelated = categorizedFindings.identity.length;
  
  return (
    <div className="min-h-screen bg-stone-900 py-8 px-4" data-testid="factual-dispute-selection">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => subType === 'data_breach' ? setSelectedBreach(null) : setSubType(null)} className="mb-4">
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          
          <h1 className="text-3xl font-bold text-white mb-2">Recommended Factual Dispute Items</h1>
          <p className="text-slate-400">
            We found items that may be disputed using a clear, plain-English approach. 
            Review the items below, confirm what applies to you, and remove anything you do not want included.
          </p>
        </div>
        
        {/* Summary Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{recommendedCount}</p>
            <p className="text-xs text-slate-400">Recommended this round</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{needsConfirmation}</p>
            <p className="text-xs text-slate-400">Need your confirmation</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{identityRelated}</p>
            <p className="text-xs text-slate-400">Identity-related</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{includedItems.size}</p>
            <p className="text-xs text-slate-400">Items included</p>
          </div>
        </div>
        
        {/* Item Sections */}
        <div className="space-y-8">
          {/* General Factual Issues */}
          {categorizedFindings.general.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText size={20} className="text-emerald-400" />
                General Factual Issues
              </h2>
              <div className="space-y-3">
                {categorizedFindings.general.map((item) => (
                  <FactualItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onUpdateDetails={updateItemDetails}
                    itemDetails={itemDetails[item.id]}
                  />
                ))}
              </div>
            </section>
          )}
          
          {/* Identity Theft / Unauthorized Activity */}
          {categorizedFindings.identity.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield size={20} className="text-amber-400" />
                Identity Theft / Unauthorized Activity
              </h2>
              <div className="space-y-3">
                {categorizedFindings.identity.map((item) => (
                  <FactualItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onUpdateDetails={updateItemDetails}
                    itemDetails={itemDetails[item.id]}
                  />
                ))}
              </div>
            </section>
          )}
          
          {/* Personal Information Issues */}
          {categorizedFindings.personalInfo.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User size={20} className="text-emerald-400" />
                Personal Information Issues
              </h2>
              <div className="space-y-3">
                {categorizedFindings.personalInfo.map((item) => (
                  <FactualItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onUpdateDetails={updateItemDetails}
                    itemDetails={itemDetails[item.id]}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
        
        {/* Navigation */}
        <div className="mt-8 flex justify-between items-center sticky bottom-4 bg-stone-900/95 backdrop-blur p-4 rounded-xl border border-slate-700">
          <div className="text-sm text-slate-400">
            <span className="text-white font-medium">{includedItems.size}</span> items selected for dispute
          </div>
          
          <Button 
            onClick={handleContinue}
            disabled={includedItems.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="continue-factual-btn"
          >
            Continue to Review
            <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FactualDisputeSelectionScreen;
